import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Fab,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  SelectChangeEvent,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCategoriesListQuery, useDocumentsListQuery, useFilesAndFoldersQuery } from 'api';
import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { ReactComponent as GridIcon } from 'assets/icons/gridView.svg';
import { ReactComponent as ListIcon } from 'assets/icons/listView.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/searchIcon.svg';
import { ReactComponent as SortAscIcon } from 'assets/icons/sort.svg';
import { ReactComponent as SortDescIcon } from 'assets/icons/sortDesc.svg';
import { GoBackButtonWithTitle, Spinner } from 'components';
import { DOCUMENTS_PAGE_LIMIT, POSTS_REQUEST_LIMIT } from 'constants/index';
import { DateRangeDropdown } from 'features/documentCategories/components/DateRangeDropdown';
import {
  ClearSearchBtn,
  CreateButton,
  FilterChip,
  FilterChipsRow,
  FilterSelect,
  SearchInput,
  SearchW,
} from 'features/documentCategories/components/DocumentCategories/styled';
import { useDocumentFilters } from 'features/documentCategories/hooks/useDocumentFilters';
import { EmptyState } from 'features/documentList/components/EmptyFiles';
import { SortMenuItem } from 'features/documentList/components/TypeFilterDropdown/styled';
import { CreateFileModal } from 'features/documents/components/CreateFileModal';
import { CreateFolderModal } from 'features/documents/components/CreateFolderModal';
import { FileCardList } from 'features/documents/components/FileCard';
import { FolderCardList } from 'features/documents/components/FolderCard';
import { SpinnerContainer } from 'features/postsList/components/PostsList/styled';
import { useAuth, useWindowSize } from 'hooks';
import theme from 'styles/theme';
import { InstanceLevelType, InstanceType } from 'types';
import { toCapitalize } from 'utils';
import { isNewsFeedOrProfileCategory, isSystemCategory } from 'validations';

import { AllPostList, FolderBreadcrumb } from '../../components';
import { TypeFilterDropdown } from '../../components/TypeFilterDropdown';
import {
  FiltersW,
  HeaderActionsW,
  HeaderRowW,
  MobileSearchRow,
  PageW,
  TabBtnW,
  TabsAndFiltersW,
  TabsW,
} from '../styled';
import { formatTag } from './formatTag';
import { Creator, FolderNavState } from './types';

type Tab = 'files' | 'posts';
const POST_FEED_TYPES = [InstanceType.news, InstanceType.document, InstanceType.polls];

export const DocumentListPage = () => {
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isManager, user: currentUser } = useAuth();
  const { isMobileX } = useWindowSize();
  const [activeTab, setActiveTab] = useState<Tab>('files');
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isCreateFileOpen, setIsCreateFileOpen] = useState(false);

  const folderId = params?.folderId;
  const employeeId = params?.employeeId;

  const folderNavState = location.state as FolderNavState | null;
  const trail = folderNavState?.trail ?? [];
  const folderName = folderNavState?.folderName;
  const isPrivate = location.pathname.includes('private-documents');
  const isPinnedSection = location.pathname.includes('pinned-posts');
  const isManagerAndNotCurrentUser = isManager && !!employeeId && currentUser?.id !== employeeId;
  const tag = params?.tag || (isPinnedSection ? 'we' : 'private');

  const { categoryMap, isLoading: isCategoryListLoading } = useCategoriesListQuery();

  const tagId = categoryMap[formatTag(tag)]?.id;

  const tagTitle = isPrivate
    ? t('profile.privateDocuments.title')
    : isPinnedSection
    ? t('we.pinnedPosts.title')
    : categoryMap[formatTag(tag)]?.label;

  const isNotResourcePage = isPinnedSection || isPrivate;
  const {
    files,
    folders,
    isLoading: isFilesLoading,
  } = useFilesAndFoldersQuery({
    categoryId: tagId,
    folderId,
  });

  useEffect(() => {
    if (isNotResourcePage) setActiveTab('posts');
  }, [isNotResourcePage]);

  const place = isPrivate ? 'profile' : isPinnedSection ? 'lastUpdated' : 'category';
  const postListQueryParams = {
    type: InstanceType.document,
    level: [InstanceLevelType.public],
    tag: tagId,
    limit: DOCUMENTS_PAGE_LIMIT,
    place,
    include_reactions: true,
  };

  const privatePostListQuery = {
    type: InstanceType.document,
    level: isManagerAndNotCurrentUser
      ? [InstanceLevelType.shared]
      : [InstanceLevelType.shared, InstanceLevelType.private],
    assigned: isManagerAndNotCurrentUser ? employeeId : '',
    limit: DOCUMENTS_PAGE_LIMIT,
    place,
    include_reactions: true,
  };

  const pinnedPostsQuery = {
    type: POST_FEED_TYPES,
    place: 'lastUpdated',
    limit: POSTS_REQUEST_LIMIT,
    pin: true,
    include_reactions: true,
  };

  const { documents, isEmptyList, lastItemRef, isFetchingNextPage, isLoading } =
    useDocumentsListQuery(
      isPrivate ? privatePostListQuery : isPinnedSection ? pinnedPostsQuery : postListQueryParams,
    );

  const {
    search: searchValue,
    setSearch: setSearchValue,
    createdById,
    setCreatedById,
    dateSort,
    setDateSort,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    sortOrder,
    setSortOrder,
    viewMode,
    setViewMode,
    toggleSort,
    uniqueFileCreators,
    uniquePostCreators,
    selectedCreatorName,
    selectedFileCreatorName,
    selectedType,
    setSelectedType,
    filteredFiles,
    filteredFolders,
    filteredDocuments: filteredPosts,
  } = useDocumentFilters({ files, folders, documents });

  const resetFilters = () => {
    setSearchValue('');
    setCreatedById('');
    setDateSort('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSortOrder('none');
    setSelectedType('');
  };

  const categoryLabel = toCapitalize(tagTitle || 'Document Category');

  const filterSelectStyle = {
    background: '#fff',
    borderRadius: '8px',
    height: '44px',
    padding: '0 12px 0px 0px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
  };

  const iconButtonStyle = {
    height: '44px',
    width: '44px',
    border: `1px solid ${theme.palette.primary.grey}`,
    borderRadius: '8px',
    padding: '8px',
    background: '#fff',
  };

  return (
    <PageW>
      <GoBackButtonWithTitle
        title={t('common.back')}
        onClick={() => navigate(-1)}
        containerSx={{
          marginTop: 0,
          maxWidth: '100%',
        }}
      />
      <HeaderRowW>
        <FolderBreadcrumb
          tag={tag}
          categoryLabel={categoryLabel}
          trail={trail}
          isPinnedSection={isPinnedSection}
          currentFolderName={folderId ? folderName || folderId : undefined}
        />
        {!isNotResourcePage && !isMobileX && (
          <HeaderActionsW>
            <SearchW>
              <SearchIcon />
              <SearchInput
                placeholder={
                  activeTab === 'posts'
                    ? t('resources.documentCategories.postSearchPlaceholder')
                    : isSystemCategory(tag)
                    ? t('resources.documentCategories.fileSearchPlaceholder')
                    : t('resources.documentCategories.fileOrFolderSearchPlaceholder')
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ width: '100%' }}
                InputProps={{ style: { height: '48px' } }}
              />
              {searchValue && <ClearSearchBtn onClick={() => setSearchValue('')}>✕</ClearSearchBtn>}
            </SearchW>

            {!isSystemCategory(categoryLabel) && activeTab === 'files' && (
              <CreateButton
                startIcon={<AddIcon style={{ fill: theme.palette.common.white }} />}
                onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
                style={{
                  height: '56px',
                  width: '125px',
                  padding: '16px',
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.common.white,
                  fontSize: '14px',
                  fontWeight: 400,
                  borderRadius: '8px',
                  textTransform: 'none',
                }}
              >
                {t('document.list.create')}
              </CreateButton>
            )}
          </HeaderActionsW>
        )}
      </HeaderRowW>

      {isMobileX && (
        <MobileSearchRow>
          <SearchW style={{ flex: 1, marginLeft: 0, maxWidth: 'none' }}>
            <SearchIcon />
            <SearchInput
              placeholder={
                activeTab === 'posts'
                  ? t('resources.documentCategories.postSearchPlaceholder')
                  : isSystemCategory(tag)
                  ? t('resources.documentCategories.fileSearchPlaceholder')
                  : t('resources.documentCategories.fileOrFolderSearchPlaceholder')
              }
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ width: '100%' }}
              InputProps={{ style: { height: '48px' } }}
            />
            {searchValue && <ClearSearchBtn onClick={() => setSearchValue('')}>✕</ClearSearchBtn>}
            <IconButton
              style={{
                height: '44px',
                width: '44px',
                marginRight: '-16px',
              }}
              onClick={() => setIsMobileFiltersOpen(true)}
            >
              <TuneIcon style={{ fontSize: '22px', opacity: 0.6 }} />
            </IconButton>
          </SearchW>
        </MobileSearchRow>
      )}

      <TabsAndFiltersW>
        {!isNotResourcePage && (
          <TabsW>
            <TabBtnW
              active={activeTab === 'files'}
              isMobileX={isMobileX}
              onClick={() => {
                setActiveTab('files');
                resetFilters();
              }}
            >
              {isSystemCategory(tag)
                ? t('document.list.files')
                : t('document.list.filesAndFolders')}
            </TabBtnW>
            {!isNewsFeedOrProfileCategory(tagTitle) && (
              <TabBtnW
                active={activeTab === 'posts'}
                isMobileX={isMobileX}
                onClick={() => {
                  setActiveTab('posts');
                  resetFilters();
                }}
              >
                {t('document.list.postsRelatedToCategory')}
              </TabBtnW>
            )}
            {isMobileX && (
              <IconButton style={{ ...iconButtonStyle, marginLeft: 'auto' }} onClick={toggleSort}>
                {sortOrder === 'asc' ? <SortAscIcon /> : <SortDescIcon />}
              </IconButton>
            )}
          </TabsW>
        )}

        <FiltersW>
          {activeTab === 'files' && (
            <TypeFilterDropdown
              value={selectedType}
              onChange={setSelectedType}
              showFolders={!isSystemCategory(categoryLabel)}
            />
          )}
          <FilterSelect
            placeholder={t('resources.documentCategories.typeLabel')}
            value={createdById}
            onChange={(e: SelectChangeEvent<unknown>) => setCreatedById(e.target.value as string)}
            style={filterSelectStyle}
            displayEmpty
          >
            <SortMenuItem value="">All</SortMenuItem>
            {(activeTab === 'files'
              ? (uniqueFileCreators as Creator[])
              : (uniquePostCreators as Creator[])
            ).map((creator) => (
              <SortMenuItem key={creator.id} value={creator.id}>
                {creator.first_name} {creator.last_name}
              </SortMenuItem>
            ))}
          </FilterSelect>

          <DateRangeDropdown
            value={dateSort}
            onChange={(val) => {
              setDateSort(val);
              if (val !== 'custom') {
                setCustomStartDate(null);
                setCustomEndDate(null);
              }
            }}
            onCustomApply={(start, end) => {
              setCustomStartDate(start);
              setCustomEndDate(end);
              setDateSort('custom');
            }}
            customStart={customStartDate}
            customEnd={customEndDate}
          />

          <IconButton style={iconButtonStyle} onClick={toggleSort}>
            {sortOrder === 'asc' ? <SortAscIcon /> : <SortDescIcon />}
          </IconButton>

          {activeTab === 'files' && (
            <IconButton
              style={iconButtonStyle}
              onClick={() => setViewMode((v: 'grid' | 'list') => (v === 'grid' ? 'list' : 'grid'))}
            >
              {viewMode === 'grid' ? <ListIcon /> : <GridIcon />}
            </IconButton>
          )}
        </FiltersW>
      </TabsAndFiltersW>
      {(dateSort || createdById || sortOrder !== 'none' || selectedType) && (
        <FilterChipsRow>
          {selectedType && (
            <FilterChip
              label={`${t('resources.documentCategories.filterType')}: ${selectedType}`}
              onDelete={() => setSelectedType('')}
            />
          )}
          {dateSort && (
            <FilterChip
              label={
                dateSort === 'custom' && customStartDate && customEndDate
                  ? `${t('resources.documentCategories.filterDate')}: ${format(
                      customStartDate,
                      'MMM d',
                    )} – ${format(customEndDate, 'MMM d, yyyy')}`
                  : `${t('resources.documentCategories.filterDate')}: ${
                      (
                        {
                          today: 'Today',
                          yesterday: 'Yesterday',
                          last7days: 'Last 7 days',
                          last30days: 'Last 30 days',
                          thisyear: 'This year',
                          lastyear: 'Last year',
                          custom: 'Custom range',
                        } as Record<string, string>
                      )[dateSort] ?? dateSort
                    }`
              }
              onDelete={() => {
                setDateSort('');
                setCustomStartDate(null);
                setCustomEndDate(null);
              }}
            />
          )}
          {createdById && (
            <FilterChip
              label={`${t('resources.documentCategories.filterCreatedBy')}: ${
                activeTab === 'files' ? selectedFileCreatorName : selectedCreatorName
              }`}
              onDelete={() => setCreatedById('')}
            />
          )}
          {sortOrder !== 'none' && (
            <FilterChip
              label={`${t('resources.documentCategories.sortOrder')}: ${
                sortOrder === 'asc' ? 'A to Z' : 'Z to A'
              }`}
              onDelete={() => setSortOrder('none')}
            />
          )}
        </FilterChipsRow>
      )}

      {(isLoading || isCategoryListLoading || isFilesLoading) && (
        <SpinnerContainer>
          <Spinner size="2.5rem" />
        </SpinnerContainer>
      )}
      {activeTab === 'posts' ? (
        <AllPostList
          documents={filteredPosts}
          isLoading={isLoading || isCategoryListLoading || isFilesLoading}
          isFetchingNextPage={isFetchingNextPage}
          isEmptyList={isEmptyList || filteredPosts.length === 0}
          lastItemRef={lastItemRef}
          tag={isNotResourcePage ? undefined : tag}
          categoriesList={[]}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {selectedType === 'Folders' ? (
            filteredFolders.length === 0 ? (
              <EmptyState />
            ) : (
              <FolderCardList
                folders={filteredFolders}
                viewMode={viewMode}
                tag={tag}
                currentFolderId={folderId}
              />
            )
          ) : selectedType ? (
            filteredFiles.length === 0 ? (
              <EmptyState />
            ) : (
              <FileCardList
                files={filteredFiles}
                viewMode={viewMode}
                tagId={tagId}
                tag={tagTitle}
              />
            )
          ) : (
            <>
              {filteredFolders.length === 0 && filteredFiles.length === 0 ? (
                <EmptyState />
              ) : (
                <FolderCardList
                  folders={filteredFolders}
                  viewMode={viewMode}
                  tag={tag}
                  currentFolderId={folderId}
                />
              )}
              <FileCardList
                files={filteredFiles}
                viewMode={viewMode}
                tagId={tagId}
                tag={tagTitle}
              />
            </>
          )}
        </div>
      )}
      {!isSystemCategory(categoryLabel) && activeTab === 'files' && !isNotResourcePage && (
        <>
          {isMobileX ? (
            <Drawer
              anchor="bottom"
              open={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              PaperProps={{
                sx: {
                  borderRadius: '20px 20px 0 0',
                  pb: '16px',
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 4,
                  borderRadius: '2px',
                  mx: 'auto',
                  my: '12px',
                }}
              />
              <SortMenuItem
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsCreateFileOpen(true);
                }}
              >
                <ListItemIcon>
                  <NoteAddOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('resources.documentCategories.addFile')}</ListItemText>
              </SortMenuItem>
              <SortMenuItem
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsCreateFolderOpen(true);
                }}
              >
                <ListItemIcon>
                  <CreateNewFolderOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('resources.documentCategories.addFolder')}</ListItemText>
              </SortMenuItem>
            </Drawer>
          ) : (
            <Menu
              anchorEl={createMenuAnchor}
              open={Boolean(createMenuAnchor)}
              onClose={() => setCreateMenuAnchor(null)}
              PaperProps={{
                sx: {
                  width: 220,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
                  mt: 1,
                },
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <SortMenuItem
                onClick={() => {
                  setCreateMenuAnchor(null);
                  setIsCreateFileOpen(true);
                }}
              >
                <ListItemIcon>
                  <NoteAddOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('resources.documentCategories.addFile')}</ListItemText>
              </SortMenuItem>
              <SortMenuItem
                onClick={() => {
                  setCreateMenuAnchor(null);
                  setIsCreateFolderOpen(true);
                }}
              >
                <ListItemIcon>
                  <CreateNewFolderOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('resources.documentCategories.addFolder')}</ListItemText>
              </SortMenuItem>
            </Menu>
          )}
          <CreateFileModal
            open={isCreateFileOpen}
            onClose={() => setIsCreateFileOpen(false)}
            categoryId={tagId}
            folderId={folderId}
          />
          <CreateFolderModal
            open={isCreateFolderOpen}
            onClose={() => setIsCreateFolderOpen(false)}
            categoryId={tagId}
            parentFolderId={folderId}
          />
          {isMobileX && isManager && (
            <Fab
              onClick={() => setIsMobileMenuOpen(true)}
              style={{
                position: 'fixed',
                bottom: '100px',
                right: '16px',
                backgroundColor: theme.palette.info.main,
                color: theme.palette.common.white,
                boxShadow: '0 4px 12px rgba(0,150,235,0.4)',
              }}
            >
              <AddIcon style={{ fill: theme.palette.common.white }} />
            </Fab>
          )}
        </>
      )}
      <Dialog
        open={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mx: '16px',
            width: 'calc(100% - 32px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '16px', pb: '8px' }}>
          {t('resources.documentCategories.searchBy')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', pt: '4px' }}>
            {activeTab === 'files' && (
              <TypeFilterDropdown
                value={selectedType}
                onChange={setSelectedType}
                showFolders={!isSystemCategory(categoryLabel)}
              />
            )}
            <FilterSelect
              placeholder={t('resources.documentCategories.typeLabel')}
              value={createdById}
              onChange={(e: SelectChangeEvent<unknown>) => setCreatedById(e.target.value as string)}
              style={filterSelectStyle}
              displayEmpty
            >
              <SortMenuItem value="">All</SortMenuItem>
              {(activeTab === 'files'
                ? (uniqueFileCreators as Creator[])
                : (uniquePostCreators as Creator[])
              ).map((creator) => (
                <SortMenuItem key={creator.id} value={creator.id}>
                  {creator.first_name} {creator.last_name}
                </SortMenuItem>
              ))}
            </FilterSelect>
          </Box>
          <Box sx={{ pt: '8px' }}>
            <DateRangeDropdown
              value={dateSort}
              onChange={(val) => {
                setDateSort(val);
                if (val !== 'custom') {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                }
              }}
              onCustomApply={(start, end) => {
                setCustomStartDate(start);
                setCustomEndDate(end);
                setDateSort('custom');
              }}
              customStart={customStartDate}
              customEnd={customEndDate}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </PageW>
  );
};
